import { apiService } from './api';
import { startAuthentication, startRegistration, browserSupportsWebAuthn } from '@simplewebauthn/browser';

/**
 * L'ecran de connexion tactile : choisir sa boutique (ou sa chambre froide,
 * ou son role sans lieu), puis sa personne, puis s'authentifier par PIN,
 * empreinte, ou mot de passe. Aucune de ces routes n'expose rien de
 * sensible avant que la personne ne soit authentifiee.
 */

export interface LieuConnexion {
  id: string;
  name: string;
  logoUrl?: string | null;
}

export interface RoleSansLieu {
  code: string;
  label: string;
}

export interface PersonneConnexion {
  userId: string;
  name: string;
  photo: string | null;
  role: string;
  hasPin: boolean;
  hasWebAuthn: boolean;
}

export const loginTactileService = {
  async getShops() {
    const r = await apiService.request<{ success: boolean; data: LieuConnexion[] }>('/auth/login-shops', { silent: true });
    return r.data || [];
  },

  async getColdStorages() {
    const r = await apiService.request<{ success: boolean; data: LieuConnexion[] }>('/auth/login-cold-storages', { silent: true });
    return r.data || [];
  },

  async getRoles() {
    const r = await apiService.request<{ success: boolean; data: RoleSansLieu[] }>('/auth/login-roles', { silent: true });
    return r.data || [];
  },

  async getPeopleForShop(shopId: string) {
    const r = await apiService.request<{ success: boolean; data: PersonneConnexion[] }>(`/auth/login-shops/${shopId}/people`, { silent: true });
    return r.data || [];
  },

  async getPeopleForColdStorage(coldStorageId: string) {
    const r = await apiService.request<{ success: boolean; data: PersonneConnexion[] }>(`/auth/login-cold-storages/${coldStorageId}/people`, { silent: true });
    return r.data || [];
  },

  async getPeopleForRole(roleCode: string) {
    const r = await apiService.request<{ success: boolean; data: PersonneConnexion[] }>(`/auth/login-roles/${roleCode}/people`, { silent: true });
    return r.data || [];
  },

  async loginByPin(userId: string, pin: string) {
    return apiService.request<any>('/auth/login-pin', {
      method: 'POST',
      body: JSON.stringify({ userId, pin }),
    });
  },

  async loginByIdPassword(userId: string, password: string) {
    return apiService.request<any>('/auth/login-id-password', {
      method: 'POST',
      body: JSON.stringify({ userId, password }),
    });
  },

  async changePin(pin: string) {
    return apiService.request<any>('/auth/pin', {
      method: 'PUT',
      body: JSON.stringify({ pin }),
    });
  },

  /**
   * Tente la connexion par empreinte pour cette personne. Rend `null` (et
   * ne montre jamais d'erreur) des que quoi que ce soit ne colle pas —
   * appareil sans capteur, aucune empreinte enregistree, geste annule : le
   * PIN reste toujours le recours normal, jamais un echec a expliquer.
   */
  async tenterConnexionEmpreinte(userId: string): Promise<any | null> {
    try {
      if (!browserSupportsWebAuthn()) return null;
      const options = await apiService.request<{ success: boolean; data: any }>('/auth/webauthn/login-options', {
        method: 'POST',
        body: JSON.stringify({ userId }),
        silent: true,
      });
      const reponseNavigateur = await startAuthentication({ optionsJSON: options.data });
      return apiService.request<any>('/auth/webauthn/login-verify', {
        method: 'POST',
        body: JSON.stringify({ userId, response: reponseNavigateur }),
      });
    } catch {
      return null;
    }
  },

  // ── Enregistrement d'un appareil, depuis le profil (deja connecte) ──

  async listerAppareils() {
    const r = await apiService.request<{ success: boolean; data: any[] }>('/auth/webauthn/devices', { silent: true });
    return r.data || [];
  },

  async enregistrerAppareil(deviceLabel?: string) {
    const options = await apiService.request<{ success: boolean; data: any }>('/auth/webauthn/register-options', { method: 'POST' });
    const reponseNavigateur = await startRegistration({ optionsJSON: options.data });
    return apiService.request<any>('/auth/webauthn/register-verify', {
      method: 'POST',
      body: JSON.stringify({ response: reponseNavigateur, deviceLabel }),
    });
  },

  async retirerAppareil(id: string) {
    return apiService.request<any>(`/auth/webauthn/devices/${id}`, { method: 'DELETE' });
  },
};
